import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AdjustIcon from '@mui/icons-material/Adjust';
import { AiOutlineMergeCells } from 'react-icons/ai';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import ExpandCircleDownRoundedIcon from '@mui/icons-material/ExpandCircleDownRounded';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { useTreeItem, UseTreeItemStatus } from '@mui/x-tree-view/useTreeItem';
import {
  TreeItemContent,
  TreeItemRoot,
  TreeItemProps,
  TreeItemGroupTransition,
  TreeItemIconContainer,
  TreeItemLabel,
} from '@mui/x-tree-view/TreeItem';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { TreeItemLabelInput } from '@mui/x-tree-view/TreeItemLabelInput';
import { CustomCheckbox } from '../../utils/CustomCheckbox';
import { ItemType } from './MapLayersForm';
import { IconButton } from '@mui/material';
import { Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { MdOutlineDonutSmall } from 'react-icons/md';

const STATUSES_OF_INTEREST: (keyof UseTreeItemStatus)[] = ['editable', 'editing', 'error', 'disabled', 'loading'];

const STATUS_ICONS: {
  [K in keyof UseTreeItemStatus]: React.ReactNode;
} = {
  focused: <AdjustIcon color="primary" fontSize="small" />,
  selected: <CheckCircleOutlinedIcon color="success" fontSize="small" />,
  expandable: <ExpandCircleDownOutlinedIcon color="secondary" fontSize="small" />,
  expanded: <ExpandCircleDownRoundedIcon color="secondary" fontSize="small" />,
  disabled: <CancelOutlinedIcon color="action" fontSize="small" />,
  editable: <EditOutlinedIcon color="warning" fontSize="small" />,
  editing: <DrawOutlinedIcon color="info" fontSize="small" />,
  loading: <HourglassBottomOutlinedIcon color="info" fontSize="small" />,
  error: <ErrorOutlineOutlinedIcon color="info" fontSize="small" />,
};

type CustomProps = {
  itemType?: ItemType;
  hiddenZones: Set<number>;
  showZone: (zoneId: number) => void;
  hideZone: (zoneId: number) => void;
  showZoneLabels: (zoneId: number) => void;
  hideZoneLabels: (zoneId: number) => void;
  zonesWithHiddenLabels: Set<number>;
  handleZoneDelete: (zoneId: number) => void;
  handleAddingPolyToZone: (zoneId: number) => void;
  handleHoleDelete: (boundaryId: number) => void;
  handleHolesMergeClick: (boundaryId: number) => void;
  handlePolysMergeClick: (zoneId: number, polyId: number) => void;
  handlePolyDelete: (polyId: number) => Promise<void>;
  hideable?: boolean;
  hasHideableLabels?: boolean;
  zoneId?: number;
  polyId?: number;
  boundaryId?: number;
  isBoundaryAreaValid?: boolean;
};

type CustomTreeItemProps = TreeItemProps & CustomProps;

const MapLayerItem = React.forwardRef<HTMLLIElement, CustomTreeItemProps>(function CustomTreeItem(
  {
    id,
    itemId,
    label,
    disabled,
    children,
    // custom props
    itemType,
    hiddenZones,
    showZone,
    hideZone,
    zonesWithHiddenLabels,
    showZoneLabels,
    hideZoneLabels,
    handleZoneDelete,
    handleAddingPolyToZone,
    handleHoleDelete,
    handleHolesMergeClick,
    handlePolysMergeClick,
    handlePolyDelete,
    hideable,
    hasHideableLabels,
    zoneId,
    polyId,
    boundaryId,
    isBoundaryAreaValid,
  }: CustomTreeItemProps,
  ref,
) {
  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getLabelProps,
    getGroupTransitionProps,
    getIconContainerProps,
    getLabelInputProps,
    status,
  } = useTreeItem({ id, itemId, label, disabled, children, rootRef: ref });

  const invalidBoundary = typeof isBoundaryAreaValid !== 'undefined' && !isBoundaryAreaValid;

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent {...getContentProps()}>
          <TreeItemIconContainer {...getIconContainerProps()}>
            <TreeItemIcon status={status} />
          </TreeItemIconContainer>

          {status.editing ? (
            <TreeItemLabelInput {...getLabelInputProps()} />
          ) : (
            <TreeItemLabel {...getLabelProps()} style={invalidBoundary ? { color: 'red' } : {}} />
          )}

          <Stack direction="row" alignItems="center" paddingRight={3}>
            {hideable && typeof zoneId !== 'undefined' && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '12px' }}>Toggle zone</span>

                <CustomCheckbox
                  checked={!hiddenZones.has(zoneId)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      showZone(zoneId);
                    } else {
                      hideZone(zoneId);
                    }
                  }}
                  color="primary"
                />
              </Box>
            )}

            {hasHideableLabels && typeof zoneId !== 'undefined' && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '12px' }}>Toggle Labels</span>

                <CustomCheckbox
                  checked={!zonesWithHiddenLabels.has(zoneId)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      showZoneLabels(zoneId);
                    } else {
                      hideZoneLabels(zoneId);
                    }
                  }}
                  color="primary"
                  disabled={hiddenZones.has(zoneId)}
                />
              </Box>
            )}

            {itemType === 'zone' && typeof zoneId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <Tooltip title={'Add poly or hole to zone'}>
                  <IconButton onClick={() => handleAddingPolyToZone(zoneId)} color="secondary" size="large">
                    <MdOutlineDonutSmall color="rgb(155, 31, 233)" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {itemType === 'zone' && typeof zoneId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <Tooltip title={'Delete zone'}>
                  <IconButton onClick={() => handleZoneDelete(zoneId)} color="secondary" size="large">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {itemType === 'inner-boundary' && typeof boundaryId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <IconButton onClick={() => handleHoleDelete(boundaryId)} color="secondary" size="large">
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            {itemType === 'inner-boundary' && typeof boundaryId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <IconButton onClick={() => handleHolesMergeClick(boundaryId)} color="secondary" size="large">
                  <AiOutlineMergeCells />
                </IconButton>
              </Box>
            )}

            {itemType === 'poly' && typeof polyId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <IconButton onClick={async () => await handlePolyDelete(polyId)} color="secondary" size="large">
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            {itemType === 'poly' && typeof polyId !== 'undefined' && typeof zoneId !== 'undefined' && (
              <Box sx={{ display: 'flex' }}>
                <Tooltip title={'Merge with another poly'}>
                  <IconButton onClick={() => handlePolysMergeClick(zoneId, polyId)} color="secondary" size="large">
                    <AiOutlineMergeCells />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {(Object.keys(STATUS_ICONS) as [keyof UseTreeItemStatus]).map((statusKey, index) => {
              if (STATUSES_OF_INTEREST.includes(statusKey) && status[statusKey]) {
                return (
                  <Box key={index} sx={{ display: 'flex' }}>
                    {STATUS_ICONS[statusKey]}
                  </Box>
                );
              }

              return null;
            })}
          </Stack>
        </TreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  );
});

export default MapLayerItem;
